import { CommonModule } from "@angular/common";
import { Component, inject, Inject, ViewEncapsulation } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRouteSnapshot } from "@angular/router";
import { MaterialModule } from "src/app/material.module";
import {
  eventAction,
  ViewContextHistoryStateService,
} from "src/app/shared/services/view-context-history-state.service";
import {
  extractSupportErrorMessage,
  recordApiRequestFailed,
  recordApiRequestStarted,
  recordApiRequestSucceeded,
  resolveSupportContextError,
  type SupportHistoryRecordOptions,
} from "src/app/shared/helpers/support-api-history.helper";
import { TranslateModule } from "@ngx-translate/core";
import { TablerIconsModule } from "angular-tabler-icons";
import { catchError, EMPTY, finalize, tap } from "rxjs";
import { StoreService } from "src/app/services/apps/store/store.service";
import { LoaderService } from "src/app/shared/components/loader/loader.service";
import {
  StoreConfigResponse,
  StoreDeliveryPayload,
  StoreGetDTO,
} from "../store";

interface DialogData {
  store: StoreGetDTO;
  storeConfig?: StoreConfigResponse;
  parentRoute?: ActivatedRouteSnapshot;
}

@Component({
  standalone: true,
  selector: "app-store-update-delivery-dialog",
  templateUrl: "./store-update-delivery-dialog.component.html",
  styleUrls: ["./store-update-delivery-dialog.component.scss"],
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    TranslateModule,
    TablerIconsModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class StoreUpdateDeliveryDialogComponent {
  deliveryForm: FormGroup;
  store: StoreGetDTO;

  private fb = inject(FormBuilder);
  private loaderSvc = inject(LoaderService);
  private storeService = inject(StoreService);
  private readonly viewContextHistorySvc = inject(
    ViewContextHistoryStateService<Record<string, unknown>>,
  );
  private readonly parentRoute = this.data.parentRoute;

  constructor(
    public dialogRef: MatDialogRef<StoreUpdateDeliveryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    this.store = { ...data.store };

    this.deliveryForm = this.fb.group({
      city: [data.storeConfig?.delivery?.city ?? "", Validators.required],
      state: [data.storeConfig?.delivery?.state ?? "", Validators.required],
      postalCode: [
        data.storeConfig?.delivery?.postalCode ?? "",
        Validators.required,
      ],
    });
  }

  submitDelivery(): void {
    if (this.deliveryForm.invalid) {
      return;
    }

    const payload: StoreDeliveryPayload = {
      city: this.deliveryForm.value.city,
      state: this.deliveryForm.value.state,
      postalCode: this.deliveryForm.value.postalCode,
    };

    this.loaderSvc.show();
    this.recordSupportHistory(
      "form_submitted",
      `User submitted delivery update for storeId=${this.store.storeId}`,
    );
    recordApiRequestStarted(
      (event, description, options) => this.recordSupportHistory(event, description, options),
      `Updating delivery metadata for storeId=${this.store.storeId}`,
    );

    this.storeService
      .updateDelivery(this.store.storeId, payload)
      .pipe(
        tap(() => {
          recordApiRequestSucceeded(
            (event, description, options) => this.recordSupportHistory(event, description, options),
            `Delivery metadata updated for storeId=${this.store.storeId}`,
          );
          this.dialogRef.close({ event: "Refresh" });
        }),
        catchError((error) => {
          console.error("Error updating delivery:", error);
          recordApiRequestFailed(
            (event, description, options) => this.recordSupportHistory(event, description, options),
            "update store delivery metadata",
            error,
            "mutation",
            { storeId: this.store.storeId },
          );
          return EMPTY;
        }),
        finalize(() => this.loaderSvc.hide()),
      )
      .subscribe();
  }

  private recordSupportHistory(
    eventAction: eventAction,
    eventDescription: string,
    options?: SupportHistoryRecordOptions,
  ): void {
    if (!this.parentRoute) {
      return;
    }

    this.viewContextHistorySvc.createContext({
      titleView: "Store Delivery Update",
      eventAction,
      eventDescription,
      thisState: { storeId: this.store.storeId, formValue: this.deliveryForm.value },
      route: this.parentRoute,
      error: resolveSupportContextError(eventAction, options?.error),
      setAutomaticallyScreenshot: false,
    });
  }
}
